import React, { useCallback, useEffect, useState } from 'react';
import { territoryService } from '../services/api';
import { TerritoryStats, TerritoryStatsRow } from '../types';
import { formatDateTime } from '../utils/datetime';
import ScopeFilter, { ScopeSelection } from './ScopeFilter';

// What is actually in each territory and domain: parties, vCons, consent
// records, accounts, recorded sessions, bytes, and when an MCP key last read
// from it.
//
// The table interleaves detail and subtotal rows — every domain of a
// territory, then that territory's total — because the question being asked is
// almost always comparative ("is this domain the whole territory, or one of
// twelve?"), and a separate summary table makes the reader hold two places at
// once.

const nf = new Intl.NumberFormat();

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function num(n: number): React.ReactNode {
  // Zero as a dim dash rather than "0": the eye should land on the cells that
  // hold something, and in a table this sparse most cells do not.
  return n ? nf.format(n) : <span style={{ opacity: 0.35 }}>—</span>;
}

const TerritoriesPage: React.FC = () => {
  const [stats, setStats] = useState<TerritoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeSelection>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await territoryService.stats({
        territory: scope.territory, domain: scope.domain,
      }));
    } catch (e) {
      setError('Could not load territory statistics');
    } finally {
      setLoading(false);
    }
  }, [scope.territory, scope.domain]);

  useEffect(() => { load(); }, [load]);

  const isSubtotal = (r: TerritoryStatsRow) => r.domain === null;

  return (
    <div className="settings-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>Territories &amp; Domains</h2>
        {stats && (
          <span className="small">
            {nf.format(stats.totals.vcons)} vCons · {formatBytes(stats.totals.bytes)} ·{' '}
            {nf.format(stats.totals.super_users)} super user
            {stats.totals.super_users === 1 ? '' : 's'}
          </span>
        )}
        <button type="button" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p className="small">
        Every recording, vCon, consent record, account and API key belongs to one
        domain, and every domain to one territory. A Super User sees all of it; a
        territory account sees one territory and all its domains; a domain account
        sees a single domain.
      </p>

      <ScopeFilter value={scope} onChange={setScope} />

      {error && <div className="error">{error}</div>}
      {loading && !stats && <div className="loading">Loading territories…</div>}
      {!stats ? null : (
      <>
      <div className="table-container">
      <table className="wide-table">
        <thead>
          <tr>
            <th>Territory</th>
            <th>Domain</th>
            <th style={{ textAlign: 'right' }}>vCons</th>
            <th style={{ textAlign: 'right' }}>Size</th>
            <th style={{ textAlign: 'right' }}>Parties</th>
            <th style={{ textAlign: 'right' }}>Consent</th>
            <th style={{ textAlign: 'right' }}>Sessions</th>
            {/* Read-only: this is where you notice a domain is set to the
                wrong language; Logs → Transcription → Languages is where it is
                changed, next to the confidence figures that show whether it
                is right. */}
            <th title="What this domain's calls are transcribed as. Set under Logs → Transcription → Languages.">
              Language
            </th>
            <th style={{ textAlign: 'right' }}>Accounts</th>
            <th style={{ textAlign: 'right' }}>MCP calls</th>
            <th>Last MCP access</th>
          </tr>
        </thead>
        <tbody>
          {stats.rows.map((r, i) => {
            const subtotal = isSubtotal(r);
            return (
              <tr
                key={`${r.territory}/${r.domain ?? '#total'}/${i}`}
                style={subtotal
                  ? { fontWeight: 600, borderTop: '2px solid rgba(128,128,128,0.4)' }
                  : undefined}
              >
                <td>
                  {subtotal ? (
                    <>
                      {r.display_name || r.territory || 'Unassigned'}
                      {r.is_readonly && (
                        <span className="small" title="Read-only: non-commercial research data"> ⚗</span>
                      )}
                    </>
                  ) : (
                    <span style={{ opacity: 0.45 }}>{r.territory}</span>
                  )}
                </td>
                <td className="monospace wrap">
                  {subtotal
                    ? <span className="small" style={{ opacity: 0.7 }}>
                        all {r.domain_count ?? 0} domain{r.domain_count === 1 ? '' : 's'}
                      </span>
                    : (r.domain || <span style={{ opacity: 0.5 }}>(unassigned)</span>)}
                </td>
                <td style={{ textAlign: 'right' }}>{num(r.vcons)}</td>
                <td style={{ textAlign: 'right' }}>{r.bytes ? formatBytes(r.bytes) : num(0)}</td>
                <td style={{ textAlign: 'right' }}>{num(r.parties)}</td>
                <td style={{ textAlign: 'right' }}>{num(r.consent_records)}</td>
                <td style={{ textAlign: 'right' }}>{num(r.sessions)}</td>
                {/* Blank on subtotal rows: a territory's domains can each be
                    set differently, and one language stated across all of them
                    would be a claim the row cannot support. */}
                <td className="small">
                  {subtotal || !r.stt_language ? (
                    <span style={{ opacity: 0.4 }}>—</span>
                  ) : (
                    <span title={`${r.stt_model} model`
                      + (r.stt_language_set ? '' : ' · follows the deployment default')}>
                      <span className="monospace">{r.stt_language}</span>
                      {!r.stt_language_set && <span style={{ opacity: 0.5 }}> (default)</span>}
                      {r.stt_model === 'default' && (
                        <span title="No telephony-trained model exists for this language — expect lower confidence on call audio"> ⚠</span>
                      )}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{num(r.users)}</td>
                <td style={{ textAlign: 'right' }}>{num(r.mcp_calls)}</td>
                <td className="small">
                  {r.last_mcp_access_at
                    ? formatDateTime(r.last_mcp_access_at)
                    : <span style={{ opacity: 0.35 }}>never</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      <p className="small">
        Accounts are counted at the level they hold: a domain account against its
        domain, a territory account against its territory's subtotal. Super users
        belong to no single territory and are counted once, in the header above.
      </p>
      </>
      )}
    </div>
  );
};

export default TerritoriesPage;
